import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { OpsBeltMark } from "@/components/OpsBeltMark";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_URL } from "@/lib/env";
import { signIn, signInWithMicrosoft, signUp, useSession } from "@/lib/auth";

type Mode = "signin" | "signup";

export function Login() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  // If we arrived mid-OIDC (a product like BlobShip sent the user here to log
  // in), resume the authorize flow after auth instead of going to the dashboard.
  // The user arrived mid-OIDC (a product like BlobShip sent them here to make/
  // use their OpsBelt account). It's an OpsBelt account either way — the screen
  // stays OpsBelt-branded; we just resume the flow back to the product after.
  const oidcResume = new URLSearchParams(location.search).has("client_id");
  function proceed() {
    if (oidcResume) {
      window.location.href = `${API_URL}/api/auth/oauth2/authorize${location.search}`;
    } else {
      navigate(from, { replace: true });
    }
  }

  // Already signed in + arrived mid-OIDC → resume immediately (no prompt).
  useEffect(() => {
    if (!isPending && session && oidcResume) {
      window.location.href = `${API_URL}/api/auth/oauth2/authorize${location.search}`;
    }
  }, [isPending, session, oidcResume, location.search]);

  // An invite link sends people to /signup?email=… — prefill + lock it so the
  // signup hook matches the invitation and joins them to the inviting org.
  const invitedEmail = new URLSearchParams(location.search).get("email");
  const [mode, setMode] = useState<Mode>(
    invitedEmail || location.pathname === "/signup" ? "signup" : "signin",
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!isPending && session && !oidcResume) return <Navigate to={from} replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res =
        mode === "signin"
          ? await signIn.email({ email, password })
          : await signUp.email({ email, password, name });
      if (res.error) {
        setError(res.error.message ?? "Authentication failed.");
        return;
      }
      proceed();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <OpsBeltMark size={32} className="text-primary" />
          <div className="leading-tight">
            <div className="font-display text-lg font-bold tracking-tight">
              Ops<span className="text-primary">Belt</span>
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">
              One login for every tool
            </div>
          </div>
        </div>

        <Card className="p-6">
          <h1 className="text-lg font-semibold">
            {mode === "signin" ? "Sign in" : "Create your account"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "signin"
              ? "Welcome back. Sign in to manage your team, billing, and tools."
              : "One OpsBelt account for BlobShip and every tool on the belt."}
          </p>

          <Button
            type="button"
            variant="secondary"
            className="mt-5 w-full"
            onClick={() =>
              signInWithMicrosoft(
                oidcResume
                  ? `${API_URL}/api/auth/oauth2/authorize${location.search}`
                  : // Must be ABSOLUTE — a bare path resolves against the auth
                    // host (api.opsbelt.com) and 404s. Anchor to this origin.
                    new URL(from, window.location.origin).toString(),
              )
            }
          >
            Continue with Microsoft
          </Button>

          <div className="my-5 flex items-center gap-3 font-mono text-[11px] text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                readOnly={!!invitedEmail}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                required
              />
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy
                ? "Working…"
                : mode === "signin"
                  ? "Sign in"
                  : "Create account"}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {mode === "signin" ? (
              <>
                Need an account?{" "}
                <button
                  className="font-medium text-primary hover:underline"
                  onClick={() => {
                    setMode("signup");
                    setError(null);
                  }}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  className="font-medium text-primary hover:underline"
                  onClick={() => {
                    setMode("signin");
                    setError(null);
                  }}
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </Card>
      </div>
    </div>
  );
}
